import { FC, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLoaderData } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpWideShort,
  faArrowDownWideShort,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons';

import { apiAddAdmin, apiRemoveAdmin, apiListAllUsers } from '../components/ui_components/ApiList';
import MainContainer from '../components/ui_components/MainContainer';
import colors from '../styles/colors';
import type { RootState, UserListItem, UserRole } from '../types';

const USERS_PER_PAGE = 15;

type SortField = 'username' | 'nickname_forum' | 'email';
type RoleFilter = 'all' | UserRole;

const ROLE_LABELS: Record<UserRole, string> = {
  founder: 'Founder',
  super_admin: 'Super Admin',
  admin: 'Admin',
  user: 'Utente',
};

// #region Styled Components
const PageWrapper = styled.div`
  width: 100%;
  padding: 20px;

  @media (max-width: 767px) {
    padding-bottom: 70px;
  }
`;

const ToolbarRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 20px;
`;

const SearchBox = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;

  & input {
    width: 100%;
    padding: 8px 12px 8px 36px;
    border: 2px solid ${colors.bgColorBordeaux};
    border-radius: 6px;
    background: ${colors.bgColorBlack2};
    color: #fff;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;

    &::placeholder {
      color: #999;
    }

    &:focus {
      border-color: gold;
    }
  }

  & .search-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    font-size: 14px;
  }
`;

const RoleSelect = styled.select`
  padding: 8px 12px;
  border: 2px solid ${colors.bgColorBordeaux};
  border-radius: 6px;
  background: ${colors.bgColorBlack2};
  color: #fff;
  font-size: 14px;
  outline: none;
  cursor: pointer;
  min-width: 160px;

  &:focus {
    border-color: gold;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  & thead th {
    background: ${colors.bgColorBordeaux};
    color: #fff;
    padding: 10px 12px;
    text-align: left;
    white-space: nowrap;
    user-select: none;
  }

  & thead th.sortable {
    cursor: pointer;

    &:hover {
      background: darkgoldenrod;
    }
  }

  & thead th .sort-icon {
    margin-left: 6px;
    font-size: 13px;
  }

  & tbody tr {
    border-bottom: 1px solid #444;
    transition: background 0.15s;

    &:hover {
      background: rgba(163, 27, 27, 0.15);
    }
  }

  & tbody td {
    padding: 8px 12px;
    vertical-align: middle;
  }

  & .wrestlers-list {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  & .wrestler-tag {
    background: ${colors.bgColorBlack2};
    border: 1px solid ${colors.bgColorBordeaux};
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 12px;
    white-space: nowrap;
  }

  & .role-badge {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
  }

  & .role-founder {
    background: gold;
    color: #000;
  }

  & .role-super_admin {
    background: gold;
    color: #000;
  }

  & .role-admin {
    background: ${colors.bgColorBordeaux};
    color: #fff;
  }

  & .role-user {
    background: #555;
    color: #fff;
  }
`;

const BtnAdmin = styled.button`
  padding: 4px 12px;
  font-size: 13px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background: ${colors.bgColorBordeaux};
  color: #fff;
  transition: background 0.2s;

  &:hover {
    background: darkgoldenrod;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationBar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  min-width: 36px;
  height: 36px;
  border: 2px solid ${(p) => (p.$active ? 'gold' : colors.bgColorBordeaux)};
  border-radius: 6px;
  background: ${(p) => (p.$active ? colors.bgColorBordeaux : colors.bgColorBlack2)};
  color: ${(p) => (p.$active ? 'gold' : '#fff')};
  font-weight: ${(p) => (p.$active ? '700' : '400')};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: ${colors.bgColorBordeaux};
    border-color: gold;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const InfoText = styled.p`
  text-align: center;
  color: #999;
  font-size: 13px;
  margin-top: 8px;
`;
// #endregion

const ListaUtenti: FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const allUsers = useLoaderData() as UserListItem[];

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [sortField, setSortField] = useState<SortField>('username');
  const [sortDesc, setSortDesc] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingAdminId, setLoadingAdminId] = useState<number | null>(null);
  const [usersData, setUsersData] = useState<UserListItem[]>(allUsers);
  const canManageAdmins = !!(user?.is_super_admin || user?.is_founder);

  const handleSortClick = (field: SortField): void => {
    if (sortField === field) {
      setSortDesc(!sortDesc);
    } else {
      setSortField(field);
      setSortDesc(false);
    }
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string): void => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleRoleFilterChange = (value: RoleFilter): void => {
    setRoleFilter(value);
    setCurrentPage(1);
  };

  const handleAddAdmin = async (userId: number): Promise<void> => {
    setLoadingAdminId(userId);
    try {
      await apiAddAdmin(userId);
      const refreshed = await apiListAllUsers();
      setUsersData(refreshed);
    } catch (error) {
      console.error('Errore durante aggiunta admin:', error);
    } finally {
      setLoadingAdminId(null);
    }
  };

  const handleRemoveAdmin = async (userId: number): Promise<void> => {
    setLoadingAdminId(userId);
    try {
      await apiRemoveAdmin(userId);
      const refreshed = await apiListAllUsers();
      setUsersData(refreshed);
    } catch (error) {
      console.error('Errore durante rimozione admin:', error);
    } finally {
      setLoadingAdminId(null);
    }
  };

  // Filtro + ricerca + ordinamento (tutto client-side)
  const processedUsers = useMemo(() => {
    let result = [...usersData];

    // Filtro per ruolo
    if (roleFilter !== 'all') {
      result = result.filter((u) => u.role === roleFilter);
    }

    // Ricerca globale
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter((u) => {
        const fields = [
          u.username,
          u.nickname_forum,
          u.email,
          ...u.wrestlers.map((w) => w.name),
        ];
        return fields.some((f) => f?.toLowerCase().includes(q));
      });
    }

    // Ordinamento
    result.sort((a, b) => {
      const valA = (a[sortField] ?? '').toString().toLowerCase();
      const valB = (b[sortField] ?? '').toString().toLowerCase();
      const cmp = valA.localeCompare(valB);
      return sortDesc ? -cmp : cmp;
    });

    return result;
  }, [usersData, roleFilter, searchQuery, sortField, sortDesc]);

  // Paginazione
  const totalPages = Math.max(1, Math.ceil(processedUsers.length / USERS_PER_PAGE));
  const paginatedUsers = processedUsers.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return (
      <FontAwesomeIcon
        icon={sortDesc ? faArrowDownWideShort : faArrowUpWideShort}
        className="sort-icon"
      />
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      if (start > 2) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }

    return (
      <PaginationBar>
        <PageBtn disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
          &laquo;
        </PageBtn>
        {pages.map((p, i) =>
          typeof p === 'string' ? (
            <span key={`ellipsis-${i}`} style={{ color: '#999', padding: '0 4px' }}>
              ...
            </span>
          ) : (
            <PageBtn
              key={p}
              $active={p === currentPage}
              onClick={() => setCurrentPage(p)}
            >
              {p}
            </PageBtn>
          )
        )}
        <PageBtn
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          &raquo;
        </PageBtn>
      </PaginationBar>
    );
  };

  return (
    <MainContainer>
      <PageWrapper>
        {/* Toolbar: ricerca + filtro ruolo */}
        <ToolbarRow>
          <SearchBox>
            <FontAwesomeIcon icon={faMagnifyingGlass} className="search-icon" />
            <input
              type="text"
              placeholder="Cerca per nome, email, forum, wrestler..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </SearchBox>

          <RoleSelect
            value={roleFilter}
            onChange={(e) => handleRoleFilterChange(e.target.value as RoleFilter)}
          >
            <option value="all">Tutti i ruoli</option>
            <option value="founder">Founder</option>
            <option value="super_admin">Super Admin</option>
            <option value="admin">Admin</option>
            <option value="user">Utente</option>
          </RoleSelect>
        </ToolbarRow>

        {/* Tabella utenti */}
        <TableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSortClick('username')}>
                  Nickname {renderSortIcon('username')}
                </th>
                <th className="sortable" onClick={() => handleSortClick('email')}>
                  Email {renderSortIcon('email')}
                </th>
                <th className="sortable" onClick={() => handleSortClick('nickname_forum')}>
                  Nome sul Forum {renderSortIcon('nickname_forum')}
                </th>
                <th>Ruolo</th>
                <th>Wrestler Assegnati</th>
                {canManageAdmins && <th>Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.length === 0 && (
                <tr>
                  <td colSpan={canManageAdmins ? 6 : 5} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                    Nessun utente trovato
                  </td>
                </tr>
              )}
              {paginatedUsers.map((singleUser) => (
                <tr key={singleUser.id}>
                  <td>{singleUser.username}</td>
                  <td>{singleUser.email}</td>
                  <td>{singleUser.nickname_forum ?? '-'}</td>
                  <td>
                    <span className={`role-badge role-${singleUser.role}`}>
                      {ROLE_LABELS[singleUser.role]}
                    </span>
                  </td>
                  <td>
                    <div className="wrestlers-list">
                      {singleUser.wrestlers.length > 0
                        ? singleUser.wrestlers.map((w) => (
                            <span key={w.id} className="wrestler-tag">
                              {w.name}
                            </span>
                          ))
                        : '-'}
                    </div>
                  </td>
                  {canManageAdmins && (
                    <td>
                      {singleUser.role === 'user' && (
                        <BtnAdmin
                          disabled={loadingAdminId === singleUser.id}
                          onClick={() => handleAddAdmin(singleUser.id)}
                        >
                          {loadingAdminId === singleUser.id ? 'Caricamento...' : 'Rendi Admin'}
                        </BtnAdmin>
                      )}
                      {singleUser.role === 'admin' && (
                        <BtnAdmin
                          disabled={loadingAdminId === singleUser.id}
                          onClick={() => handleRemoveAdmin(singleUser.id)}
                          style={{ background: '#dc3545' }}
                        >
                          {loadingAdminId === singleUser.id ? 'Caricamento...' : 'Rimuovi Admin'}
                        </BtnAdmin>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableWrapper>

        {/* Info e Paginazione */}
        <InfoText>
          {processedUsers.length} utent{processedUsers.length === 1 ? 'e' : 'i'} trovati
          {processedUsers.length > USERS_PER_PAGE &&
            ` — Pagina ${currentPage} di ${totalPages}`}
        </InfoText>
        {renderPagination()}
      </PageWrapper>
    </MainContainer>
  );
};

export default ListaUtenti;

export async function loader(): Promise<UserListItem[]> {
  try {
    return await apiListAllUsers();
  } catch (error) {
    throw new Error('Failed to load users');
  }
}
